import uuidv4 from 'uuid/v4';
import basic from './common/basic.js';
import rect from './common/rect.js';
import { unwrapMention, setMentionContent } from '../mention-helper.js';
import updater from '../updater.js';
import { roundToNearestMultiple } from '../../../utils.js';

/* State example:
state: {
  byId: {
    "note_1": {
      "x": 10205,
      "y": 10469,
      "id": "note_1",
      "theme": "cyan",
      "width": 138,
      "height": 50,
      "autoHeight": true,
      "content": "",
    },
    "note_2": {
      "x": 10425,
      "y": 10293,
      "id": "note_2",
      "theme": "purple",
      "width": 410,
      "height": 387,
      "autoHeight": false,
      "content": "<p><i>Day, outside a village inn, west England 1850 A.D.</i></p><span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;is doing his daily chores at the <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_10\" data-type=\"component\">Admiral Benbow Inn</span>&nbsp;when&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;arrives and demands infomation about a patron,&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_11\" data-type=\"component\">Billy Bones</span>.<p>When <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;tries to escape, <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;strikes him with the hilt of his cutlass and he loses consciousness.</p><p><i>Fade to Black.</i></p><p></p>",
    },
  }
}
*/
const NEW_NOTE = {
  content: '',
  width: 301,
  height: 101,
  autoHeight: true,
  theme: 'default',
};
const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...rect.getters(options),
};

const actions = {
  ...basic.actions({
    type: 'notes',
    container: { type: 'boards', prop: 'notes' },
  }),

  /**
   * Create a new default note
   * @param {Object}  boardId   The board into which that will be created
   * @param {Object}  position  The position onto the board
   * @param {Object}  props     Customization to the default values
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  customCreate(context, { boardId, position, props = {}, apply }) {
    const id = uuidv4();
    let x = position.x - NEW_NOTE.width / 2;
    let y = position.y - NEW_NOTE.height / 2;

    // Constrain the note position to never leave the board limits
    x = Math.max(x, 0);
    y = Math.max(y, 0);
    x = Math.min(x, 20000 - NEW_NOTE.width);
    y = Math.min(y, 20000 - NEW_NOTE.height);

    const { snap, size } = context.rootGetters['project/config/getGrid'];
    if (snap) {
      x = roundToNearestMultiple(x, size);
      y = roundToNearestMultiple(y, size);
    }
    // Apply also custom default styles
    const defaultStyle =
      context.rootGetters['project/config/notes/getDefaultStyle'];
    const note = { id, ...NEW_NOTE, ...defaultStyle, x, y, ...props };
    context.dispatch('create', {
      node: note,
      tId: boardId,
      tIndex: '-',
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the note's properties (note id should be filled)
   * @param {String}  id    The note id
   * @param {Object}  props Contains only the properties that are changed
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Remove a note with specified id from the store
   * @param {String}  id      The note id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Delete the note
    context.dispatch('delete', { id, apply: false });
    // Deselect it
    context.dispatch(
      'project/config/notes/deselect',
      { id, apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Remove mentions in all notes.
   * @param {array}  ids        The ids of the mentioned objects
   * @param {String} type       The type of the mentioned object
   */
  deleteMentions(context, { ids, type, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(note => {
      const text = unwrapMention(note.content, ids, type);
      if (text !== note.content) {
        patch.push({
          op: 'replace',
          path: `/data/notes/byId/${note.id}/content`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the name in mentions in all notes.
   * @param {String} id       The id of the mentioned object
   * @param {String} type     The type of the mentioned object
   * @param {String} name     The new name of the mentioned object
   * @param {String}  label The original label of the mention if different from name
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(note => {
      const text = setMentionContent(note.content, id, type, name, label);
      if (text !== note.content) {
        patch.push({
          op: 'replace',
          path: `/data/notes/byId/${note.id}/content`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
